
import React, { useState, useEffect, useCallback } from 'react';
import { useStudents } from "@/contexts";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Eye, Edit, Trash, Printer } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { differenceInDays } from "date-fns";
import { Input } from "@/components/ui/input";
import { printStudentsListPaginatedAsJPG, printStudentsListPaginatedAsPDF } from "@/utils/printing/studentListPaginatedPrinter";
import PageSelectionDialog from "@/components/students/PageSelectionDialog";
import StudentsPagination from "@/components/students/StudentsPagination";
import { enhancedToast } from '@/utils/notifications/enhancedToast';

const STUDENTS_PER_PAGE = 12;

const TotalStudentsPage = () => {
  const { students, loading } = useStudents();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [focusedRowIndex, setFocusedRowIndex] = useState(-1);
  const [isPrinting, setIsPrinting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isPageDialogOpen, setIsPageDialogOpen] = useState(false);

  const filteredStudents = students.filter(student => 
    student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.programs.some(p => p.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (student.fathersName || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate pagination
  const totalStudentsForTable = filteredStudents.reduce((acc, student) => acc + student.programs.length, 0);
  const totalPages = Math.ceil(totalStudentsForTable / STUDENTS_PER_PAGE);
  const startIndex = (currentPage - 1) * STUDENTS_PER_PAGE;
  const endIndex = startIndex + STUDENTS_PER_PAGE;

  // Get paginated data for table display
  const paginatedStudentsForTable = (() => {
    const allStudentPrograms = filteredStudents.flatMap(student => 
      student.programs.map(program => ({ student, program }))
    );
    return allStudentPrograms.slice(startIndex, endIndex);
  })();

  const getRemainingDays = (completionDate: string | Date) => {
    const remaining = differenceInDays(new Date(completionDate), new Date());
    return remaining > 0 ? remaining : 0;
  };

  const handlePrintStudentsList = () => {
    setIsPageDialogOpen(true);
  };

  const handleDownloadPages = async (format: 'jpg' | 'pdf', startPage: number, endPage: number) => {
    if (isPrinting) return;
    
    setIsPrinting(true);
    const toastId = enhancedToast.loading("Preparing students list for download...");
    
    try {
      const studentsList = filteredStudents.map(student => 
        student.programs.map(program => ({
          id: student.id,
          name: student.name,
          fathersName: student.fathersName || 'N/A',
          programCategory: program.category,
          programTitle: program.title,
          fees: program.fee,
          paymentStatus: program.feeStatus,
          remainingDays: getRemainingDays(program.completionDate)
        }))
      ).flat();
      
      if (format === 'jpg') {
        await printStudentsListPaginatedAsJPG(studentsList, startPage, endPage);
      } else {
        await printStudentsListPaginatedAsPDF(studentsList, startPage, endPage);
      }
      
      enhancedToast.success(`Students list downloaded as ${format.toUpperCase()} successfully!`);
      enhancedToast.dismiss(toastId);
    } catch (error) {
      console.error('Error downloading students list:', error);
      enhancedToast.error("Failed to download students list");
      enhancedToast.dismiss(toastId);
    } finally {
      setIsPrinting(false);
    }
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (searchQuery) {
        setSearchQuery('');
      } else {
        navigate('/');
      }
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedRowIndex(prev => {
        const totalRows = paginatedStudentsForTable.length;
        const newIndex = e.key === 'ArrowDown' 
          ? Math.min(prev + 1, totalRows - 1)
          : Math.max(prev - 1, 0);
        return newIndex;
      });
    }

    if (e.key === 'Enter' && focusedRowIndex >= 0) {
      const selectedItem = paginatedStudentsForTable[focusedRowIndex];
      if (selectedItem) {
        navigate(`/students/${selectedItem.student.id}`);
      }
    }
  }, [searchQuery, navigate, paginatedStudentsForTable, focusedRowIndex]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleSearchFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    if (window.innerWidth <= 768) {
      setTimeout(() => {
        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">All Students</h2>
        <div className="flex items-center gap-4">
          <Button 
            onClick={() => navigate('/')}
            variant="outline"
            size="sm"
            aria-label="Back to Dashboard"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <Button 
            onClick={() => navigate('/students/new')}
            size="sm"
            aria-label="Add New Student"
          >
            + Add New Student
          </Button>
          <Button 
            onClick={handlePrintStudentsList}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            disabled={isPrinting || totalStudentsForTable === 0}
          >
            <Printer className="h-4 w-4" />
            {isPrinting ? 'Processing...' : 'Print List'}
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-lg shadow">
        <div className="p-4">
          <Input
            type="search"
            placeholder="Search students by name, father's name or program... (Press '/' to focus)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-xl"
            onFocus={handleSearchFocus}
            aria-label="Search students"
          />
        </div>

        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Father's Name</TableHead>
                <TableHead>Program Category</TableHead>
                <TableHead>Program Title</TableHead>
                <TableHead>Fees</TableHead>
                <TableHead>Payment Status</TableHead>
                <TableHead>Remaining Days</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedStudentsForTable.map(({ student, program }, index) => (
                <TableRow 
                  key={`${student.id}-${program.title}-${index}`}
                  className={focusedRowIndex === index ? 'bg-muted' : ''}
                  tabIndex={0}
                  role="row"
                  aria-selected={focusedRowIndex === index}
                >
                  <TableCell>{student.id}</TableCell>
                  <TableCell className="font-medium">{student.name}</TableCell>
                  <TableCell>{student.fathersName || "N/A"}</TableCell>
                  <TableCell>{program.category}</TableCell>
                  <TableCell>{program.title}</TableCell>
                  <TableCell>${program.fee}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      program.feeStatus === 'paid' ? 'bg-green-100 text-green-800' :
                      program.feeStatus === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {program.feeStatus}
                    </span>
                  </TableCell>
                  <TableCell>{getRemainingDays(program.completionDate)} days</TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Link to={`/students/${student.id}`}>
                        <Button 
                          variant="outline" 
                          size="sm"
                          aria-label={`View details for ${student.name}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Link to={`/students/edit/${student.id}`}>
                        <Button 
                          variant="outline" 
                          size="sm"
                          aria-label={`Edit ${student.name}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t">
            <StudentsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        {/* Page info */}
        <div className="p-4 text-sm text-muted-foreground text-center border-t">
          Showing {startIndex + 1} to {Math.min(endIndex, totalStudentsForTable)} of {totalStudentsForTable} student programs
        </div>
      </div>

      {/* Page Selection Dialog */}
      <PageSelectionDialog
        isOpen={isPageDialogOpen}
        onClose={() => setIsPageDialogOpen(false)}
        totalStudents={totalStudentsForTable}
        onDownload={handleDownloadPages}
      />
    </div>
  );
};

export default TotalStudentsPage;
